// ============================================================
// NewProjectModal — visible, usable project creation entry point.
//
// Live: POSTs to /api/projects/create and navigates the caller
// to the new project's id.
//
// Demo (VITE_OLIVIUM_MODE !== 'live'): synthesizes a local id and
// records a Sentry breadcrumb so the user still gets a finished
// interaction. Demo rows are clearly labeled by useProjects().
// ============================================================

import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { recordShellAction } from '../../observability/sentry'
import { SHELL_TOKENS, text } from '../../shell/styles'
import { api, ApiError } from '../../api/client'
import { getRuntime } from '../../hooks/_mode'
import { getProjectPreset } from './projectPresets'

interface NewProjectModalProps {
  onClose: () => void
  onCreated?: (id: string) => void
}

function slugify(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

export default function NewProjectModal({ onClose, onCreated }: NewProjectModalProps) {
  // Optional, opt-in preset. When the modal is opened with ?preset=<key>
  // (e.g. /square/projects/new?preset=olivium-commandery) it pre-fills the
  // fields and attaches the preset's metadata_json on create. With no
  // preset the modal is unchanged: metadata_json is omitted and the server
  // defaults it to {} — existing projects are unaffected.
  const [searchParams] = useSearchParams()
  const preset = getProjectPreset(searchParams.get('preset'))

  const [name, setName] = useState(preset?.name ?? '')
  const [clientName, setClientName] = useState(preset?.client_name ?? '')
  const [domain, setDomain] = useState(preset?.domain ?? '')
  const [slug, setSlug] = useState(preset?.slug ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const computedSlug = slug.trim() || slugify(name)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Name is required')
      return
    }
    if (!computedSlug) {
      setError('Slug could not be derived from the name')
      return
    }
    setSubmitting(true)

    recordShellAction('project.create_intent', {
      name: name.trim(),
      client_name: clientName.trim() || null,
      domain: domain.trim() || null,
      slug: computedSlug,
      preset: preset?.key ?? null,
    })

    const { live } = getRuntime()

    try {
      if (live) {
        const result = await api.projects.create({
          name: name.trim(),
          slug: computedSlug,
          client_name: clientName.trim() || undefined,
          domain: domain.trim() || undefined,
          // Preset-only: status + operator metadata_json. Both are
          // undefined for a normal manual create, so the server applies
          // its defaults (status 'active', metadata_json {}).
          status: preset?.status,
          metadata_json: preset?.metadata_json,
        })
        if (onCreated) onCreated(result.project.id)
        onClose()
      } else {
        // Demo branch — no backend reachable. Synthesize a local id
        // so the user sees a finished interaction; the row is clearly
        // a demo row in useProjects().
        const localId = `demo-new-${computedSlug}-${Date.now().toString(36)}`
        if (onCreated) onCreated(localId)
        onClose()
      }
    } catch (err) {
      const apiErr = err as ApiError
      const code = apiErr?.code
      if (code === 'slug_taken') {
        setError('A project with this slug already exists.')
      } else {
        setError(apiErr?.message || 'create failed')
      }
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,8,12,0.78)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 460,
          background: SHELL_TOKENS.voidAlt,
          border: `1px solid ${SHELL_TOKENS.borderAlt}`,
          borderRadius: 14,
          padding: 24,
          color: SHELL_TOKENS.ink,
          fontFamily: 'inherit',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}
      >
        <div
          style={{
            ...text.kicker,
            fontSize: 9,
            letterSpacing: '0.22em',
            color: SHELL_TOKENS.inkFaint,
            marginBottom: 6,
          }}
        >
          NEW PROJECT
        </div>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 700,
            margin: 0,
            marginBottom: 18,
            color: SHELL_TOKENS.ink,
            fontFamily: 'var(--font-brand)',
          }}
        >
          Create a project
        </h2>

        {preset && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              borderRadius: 8,
              border: `1px solid ${SHELL_TOKENS.brandLine}`,
              background: SHELL_TOKENS.brandSoft,
              color: SHELL_TOKENS.inkDim,
              fontSize: 11,
              lineHeight: 1.5,
              fontFamily: 'var(--font-mono)',
            }}
          >
            Preset <strong style={{ color: SHELL_TOKENS.brand }}>{preset.name}</strong> ·
            operator <code>metadata_json</code> ({(preset.metadata_json.labels as string[] | undefined)?.length ?? 0} labels)
            will be attached on create. Fields are editable before you submit.
          </div>
        )}

        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (!slug) setSlug(slugify(e.target.value))
            }}
            placeholder="Acme Renovations"
            autoFocus
            style={inputStyle}
          />
        </Field>

        <Field label="Client Name">
          <input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Optional display name"
            style={inputStyle}
          />
        </Field>

        <Field label="Slug">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(slugify(e.target.value))}
            placeholder={slugify(name) || 'auto-generated'}
            style={inputStyle}
          />
        </Field>

        <Field label="Domain">
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="client.example.com"
            style={inputStyle}
          />
        </Field>

        {error && (
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              color: SHELL_TOKENS.redText,
              fontFamily: 'var(--font-mono)',
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 22,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={secondaryBtn}
          >
            Cancel
          </button>
          <button type="submit" disabled={submitting} style={primaryBtn}>
            {submitting ? 'Creating…' : 'Create Project'}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          ...text.kicker,
          fontSize: 8,
          letterSpacing: '0.18em',
          color: SHELL_TOKENS.inkFaint,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: `1px solid ${SHELL_TOKENS.border}`,
  background: SHELL_TOKENS.surface,
  color: SHELL_TOKENS.ink,
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
}

const primaryBtn: React.CSSProperties = {
  padding: '9px 16px',
  borderRadius: 8,
  border: `1px solid ${SHELL_TOKENS.brandLine}`,
  background: SHELL_TOKENS.brandSoft,
  color: SHELL_TOKENS.brand,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontFamily: 'var(--font-mono)',
}

const secondaryBtn: React.CSSProperties = {
  padding: '9px 16px',
  borderRadius: 8,
  border: `1px solid ${SHELL_TOKENS.border}`,
  background: SHELL_TOKENS.surface,
  color: SHELL_TOKENS.inkDim,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontFamily: 'var(--font-mono)',
}
